// client/src/components/inventory/SupplierSelector.tsx
import { useEffect, useState } from "react";
import { getShopSuppliers } from "@/lib/api";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Label } from "@/components/ui/label";
import { Loader2 } from "lucide-react";
import { useToast } from "@/hooks/use-toast";

const suppliersCache = new Map<string, { suppliers: any[]; timestamp: number }>();
const CACHE_TTL = 30000;

interface SupplierSelectorProps {
  shopId: string;
  value?: string; // supplierId
  onValueChange: (value: string) => void;
  placeholder?: string;
  className?: string;
  label?: string;
  required?: boolean;
  onCreateSupplier?: () => void;
}

export default function SupplierSelector({ 
  shopId, 
  value, 
  onValueChange, 
  placeholder = "Seleccionar proveedor...",
  className = "",
  label = "Proveedor",
  required = false,
  onCreateSupplier
}: SupplierSelectorProps) {
  const [suppliers, setSuppliers] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const { toast } = useToast();

  useEffect(() => {
    const loadSuppliers = async () => {
      try {
        setLoading(true);
        const cached = suppliersCache.get(shopId);
        if (cached && Date.now() - cached.timestamp < CACHE_TTL) {
          setSuppliers(cached.suppliers);
          setLoading(false);
          return;
        }
        const result = await getShopSuppliers(shopId);
        if (result.success) {
          setSuppliers(result.suppliers || []);
          suppliersCache.set(shopId, { suppliers: result.suppliers || [], timestamp: Date.now() });
        }
      } catch (error: any) {
        console.error("Error cargando proveedores:", error);
        toast({
          title: "Error",
          description: "No se pudieron cargar los proveedores",
          variant: "destructive"
        });
      } finally {
        setLoading(false);
      }
    };

    if (shopId) {
      loadSuppliers();
    }
  }, [shopId, toast]);

  if (loading) {
    return (
      <div className="space-y-2">
        {label && <Label>{label}{required && " *"}</Label>}
        <div className="flex items-center justify-center p-4 border rounded-md">
          <Loader2 className="h-4 w-4 animate-spin mr-2" />
          <span className="text-sm text-muted-foreground">Cargando proveedores...</span>
        </div>
      </div>
    );
  }

  return (
    <div className={`space-y-2 ${className}`}>
      {label && (
        <Label htmlFor="supplier-selector">
          {label}
          {required && <span className="text-destructive ml-1">*</span>}
        </Label>
      )}
      
      <Select 
        value={value === "" ? "null" : (value || "null")}  // Manejar empty string
        onValueChange={(newValue) => {
          // Convertir "null" de vuelta a empty string
          onValueChange(newValue === "null" ? "" : newValue);
        }}
        disabled={suppliers.length === 0}
       >
        <SelectTrigger id="supplier-selector">
          <SelectValue placeholder={suppliers.length === 0 ? "No hay proveedores" : placeholder} />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="null">Sin proveedor</SelectItem>
          {suppliers.map((supplier) => (
            <SelectItem key={supplier.id} value={supplier.id}>
              <span className="font-medium">{supplier.name}</span>
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      
      {suppliers.length === 0 && !loading && (
        <p className="text-xs text-muted-foreground">
          No hay proveedores registrados.{" "}
          <button
            type="button"
            className="text-primary hover:underline ml-1 inline"
            onClick={() => onCreateSupplier?.()}
          >
            Crear proveedor
          </button>
        </p>
      )}
    </div>
  );
}